import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { formatDuration, voiceNoteObjectUrl, voiceNotePeaks } from "../game/voice-note";
import { faTime } from "../i18n/digits";
import { fa } from "../i18n/fa";

/** Play / pause / loading glyphs — stroke/fill = currentColor so they adapt to
 * both the incoming (surface) and outgoing (primary) bubble. */
function Glyph({ state }: { state: "idle" | "playing" | "loading" }): ReactNode {
  if (state === "loading") {
    return (
      <span
        aria-hidden="true"
        className="h-[15px] w-[15px] animate-spin rounded-full border-2"
        style={{
          borderColor: "color-mix(in srgb, currentColor 30%, transparent)",
          borderTopColor: "currentColor",
        }}
      />
    );
  }
  return (
    <svg viewBox="0 0 24 24" width={16} height={16} fill="currentColor" aria-hidden="true">
      {state === "playing" ? (
        <>
          <rect x="6" y="5" width="4" height="14" rx="1" />
          <rect x="14" y="5" width="4" height="14" rx="1" />
        </>
      ) : (
        <path d="M8 5v14l11-7z" />
      )}
    </svg>
  );
}

/**
 * The bubble's real waveform: one rounded bar per peak bucket, drawn in
 * `currentColor` so it reads on either side's surface. Played progress fills
 * bar-by-bar — the DOM order follows the container's RTL flow, so playback
 * fills right → left like every Persian voice bubble. Purely decorative
 * (`aria-hidden`); the play button + mm:ss carry the semantics.
 */
function WaveBars({ peaks, progress }: { peaks: readonly number[]; progress: number }): ReactNode {
  const played = Math.round(progress * peaks.length);
  return (
    <div aria-hidden="true" data-testid="voice-wave" className="flex h-full items-center gap-px">
      {peaks.map((p, i) => (
        <span
          key={i}
          data-played={i < played ? "true" : "false"}
          className="min-w-[2px] flex-1 rounded-full"
          style={{
            height: `${Math.round(Math.max(0.16, p) * 100)}%`,
            background:
              i < played ? "currentColor" : "color-mix(in srgb, currentColor 30%, transparent)",
          }}
        />
      ))}
    </div>
  );
}

/**
 * A voice-note chat bubble: a play/pause control, the decoded WAVEFORM with a
 * played-progress fill (falling back to a slim progress bar until the clip is
 * first loaded — or forever on WebViews whose `decodeAudioData` can't read the
 * codec), the duration (mm:ss) and a subtle `HH:MM` time caption. Sides follow
 * the shared bubble convention (sent → right/`self-start`, received →
 * left/`self-end`, RTL) with the tokened surfaces and the outer-edge tail;
 * inner controls stay `currentColor`-relative so they read on either side.
 * The waveform is decoded eagerly ON LOAD ({@link voiceNotePeaks}, cached per id
 * — re-renders and re-mounts never re-decode, and a sender's primed clip shows
 * with no download) so the real bars paint before the user ever plays; the audio
 * itself is fetched lazily on first play ({@link voiceNoteObjectUrl}, sharing
 * that same cached download) and one `<audio>` element drives playback with a
 * load spinner.
 */
export function VoiceBubble({
  voiceId,
  durationMs,
  mine,
  at,
}: {
  voiceId: string;
  durationMs: number;
  mine: boolean;
  /** Epoch ms the message was sent — renders the subtle time caption. */
  at?: number;
}): ReactNode {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const loadedRef = useRef(false);
  const mountedRef = useRef(true);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(false);
  // 0..1 — from the audio once loaded, so the fill tracks real playback.
  const [progress, setProgress] = useState(0);
  // Decoded waveform buckets; null = not (yet) available → slim-bar fallback.
  const [peaks, setPeaks] = useState<readonly number[] | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Paint the REAL waveform as soon as the bubble loads — NOT lazily on first
  // play. `voiceNotePeaks` fetches (shared with playback) + decodes once, and
  // returns the per-id cache on repeat/re-mount, so a sender's primed clip or an
  // already-seen note shows instantly with no re-download. A decode failure
  // (no WebAudio / unreadable codec) rejects → peaks stays null → the slim bar.
  useEffect(() => {
    void voiceNotePeaks(voiceId)
      .then((p) => {
        if (mountedRef.current) setPeaks(p);
      })
      .catch(() => undefined);
  }, [voiceId]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay = (): void => setPlaying(true);
    const onPause = (): void => setPlaying(false);
    const onTime = (): void => {
      if (audio.duration > 0) setProgress(Math.min(1, audio.currentTime / audio.duration));
    };
    const onEnded = (): void => {
      setPlaying(false);
      setProgress(0);
      audio.currentTime = 0;
    };
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const toggle = (): void => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      return;
    }
    setError(false);
    if (loadedRef.current) {
      void audio.play().catch(() => undefined);
      return;
    }
    setLoading(true);
    void voiceNoteObjectUrl(voiceId)
      .then((url) => {
        loadedRef.current = true;
        audio.src = url;
        setLoading(false);
        return audio.play();
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  };

  const glyph: "idle" | "playing" | "loading" = loading ? "loading" : playing ? "playing" : "idle";

  // RTL "tail": tighten the bottom corner on the bubble's OUTER edge — sent
  // bubbles sit right (inline start), received ones left (inline end).
  const tail: CSSProperties = mine ? { borderEndStartRadius: 4 } : { borderEndEndRadius: 4 };

  return (
    <div
      data-mine={mine ? "true" : "false"}
      className={[
        "flex min-w-[176px] max-w-[78%] items-center gap-2.5 rounded-2xl px-3 py-2",
        mine
          ? "self-start bg-primary-container text-on-primary-container"
          : "self-end bg-surface-container-high text-on-surface",
      ].join(" ")}
      style={tail}
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? fa.chat.pauseVoice : fa.chat.playVoice}
        className="grid h-9 w-9 flex-none cursor-pointer place-items-center rounded-full border-0 text-inherit"
        style={{ background: "color-mix(in srgb, currentColor 16%, transparent)" }}
      >
        <Glyph state={glyph} />
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/*
          ⚠️ ONE SLOT, FIXED HEIGHT, ALWAYS RENDERED — only its CONTENTS swap.
          The waveform decodes ASYNCHRONOUSLY (see the `voiceNotePeaks` effect above), so it lands
          after the thread has painted and pinned. When the bars owned `h-5` and the fallback `h-1`,
          every bubble GREW 16px at that moment; the chat log's content ResizeObserver saw it,
          debounced 120 ms and re-pinned — one delayed jump, and with six voice notes in a thread,
          six bubbles' worth of growth. Reserving the box here makes «same height» structural: there
          is one height, in one place, and no second branch that could disagree with it.
        */}
        <div data-testid="voice-slot" className="flex h-5 items-center">
          {peaks && peaks.length > 0 ? (
            <WaveBars peaks={peaks} progress={progress} />
          ) : (
            /* Pre-load / no-WebAudio fallback: the original slim bar, centred in the reserved slot. */
            <div
              aria-hidden="true"
              data-testid="voice-progress"
              className="relative w-full overflow-hidden rounded-full"
              style={{
                height: 4,
                background: "color-mix(in srgb, currentColor 24%, transparent)",
              }}
            >
              <div
                className="absolute inset-y-0 rounded-full"
                style={{
                  insetInlineStart: 0,
                  width: `${progress * 100}%`,
                  background: "currentColor",
                  transition: "width .1s linear",
                }}
              />
            </div>
          )}
        </div>
        <span className="flex items-baseline justify-between gap-2">
          <span className="text-caption font-bold tabular-nums opacity-80">
            {error ? fa.chat.voiceLoadError : formatDuration(durationMs)}
          </span>
          {at ? (
            <span
              dir="ltr"
              className="font-caption text-[10px] leading-none opacity-60"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {faTime(at)}
            </span>
          ) : null}
        </span>
      </div>

      {/* One hidden audio element per bubble; src is set lazily on first play. */}
      <audio ref={audioRef} preload="none" aria-label={fa.chat.voiceNote} />
    </div>
  );
}
